import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { TmCart, TmCartOrderSummary } from '@/tscapi/model/cart';
import { TmModifyOrderRequest, TmAddItemRequest } from '@/tscapi/model/request/order-request';
import http from '@/tscapi/service/httpservice';
import { Cookie } from '@/tscapi/utils/cookie';

export function useOrderReview(isAuth: boolean, modifyOrderNumber: string, cart: TmCart) {
    const cookie: Cookie = new Cookie();

    //prepare put request payload (current cart contents)
    const orderRequest: TmModifyOrderRequest = new TmModifyOrderRequest();
    if (cookie.ModifyOrderPromocode) {
        orderRequest.Promocode = cookie.ModifyOrderPromocode;
    } else {
        delete orderRequest.Promocode;
    }
    orderRequest.Items = [];
    for (let i = 0; i < cart?.CartLines?.length; i++) {
        const cartLine = cart.CartLines[i];
        const itemRequest: TmAddItemRequest = new TmAddItemRequest();
        itemRequest.ItemEdp = cartLine.CartLineItem.EdpNo.toString();
        itemRequest.ItemQuantity = cartLine.InCart.Quantity;
        orderRequest.Items.push(itemRequest);
    }
    //end put request payload preparation

    return useQuery({
        queryKey: [
            'orderreview',
            cookie.CartGuid,
            modifyOrderNumber,
            orderRequest.Items,
            orderRequest.Promocode ? orderRequest.Promocode : '',
        ],
        queryFn: () => fetchOrder(modifyOrderNumber),
        enabled: !!modifyOrderNumber && modifyOrderNumber.length > 0 && !!cart,
        useErrorBoundary: true,
        retry: false,
        //staleTime: 1000 * 60 * 5, //5mins
        staleTime: 1000 * 5, //30 secondz
        refetchOnWindowFocus: false,
    });

    async function fetchOrder(modifyOrderNumber): Promise<TmCartOrderSummary> {
        const base = '/api/v2/';
        const lang = cookie.Language.substring(0, 2) || 'en';
        const userAccessTokenEdp = cookie.UserAccessToken?.Edp;
        const headers = {
            'Content-Type': 'application/json',
            'cache-control': 'no-cache',
            currentdatetime: cookie.CurrentDate,
        };

        try {
            const response = await http.put(
                `${base}${lang}/accounts/${userAccessTokenEdp}/orders/${modifyOrderNumber}/review`,
                orderRequest,
            );
            //console.log(response.data, 'order review returned');
            setupAnalyticsData(cart, response.data);
            return response.data as TmCartOrderSummary;
        } catch (error) {
            //console.log(error);
            //console.log(error.response);
            //console.log('Error getting Order');
            cookie.deleteModifyOrder_Promocode();
            throw new Error('Error getting Order');
        }
    }
}

export const useUpdatePromo = (modifyOrderNumber: string, cart: TmCart) => {
    const base = '/api/v2/';
    const queryClient = useQueryClient();
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    const userAccessTokenEdp = cookie.UserAccessToken?.Edp;

    //prepare put request payload (current cart contents)
    const orderRequest: TmModifyOrderRequest = new TmModifyOrderRequest();
    orderRequest.Items = [];
    for (let i = 0; i < cart?.CartLines.length; i++) {
        const cartLine = cart.CartLines[i];
        const itemRequest: TmAddItemRequest = new TmAddItemRequest();
        itemRequest.ItemEdp = cartLine.CartLineItem.EdpNo.toString();
        itemRequest.ItemQuantity = cartLine.InCart.Quantity;
        orderRequest.Items.push(itemRequest);
    }
    //end put request payload preparation

    return {
        ...useMutation({
            mutationFn: (promoCode: string) => {
                // if (promoCode == null || promoCode == '') {
                //     delete orderRequest.Promocode;
                // } else {
                //     orderRequest.Promocode = promoCode;
                // }
                orderRequest.Promocode = promoCode.trim();
                return http.put(
                    `${base}${lang}/accounts/${userAccessTokenEdp}/orders/${modifyOrderNumber}/review`,
                    orderRequest,
                );
            },
            onSuccess: (response) => {
                // update detail view directly
                //console.log(response.data, 'order review updated');
                const respData = response.data as TmCartOrderSummary;
                if (respData.AppliedPromocode != null && respData.AppliedPromocode != '') {
                    cookie.ModifyOrderPromocode = respData.AppliedPromocode;
                } else {
                    cookie.deleteModifyOrder_Promocode();
                }
                setupAnalyticsData(cart, response.data);
                queryClient.setQueryData(
                    [
                        'orderreview',
                        cookie.CartGuid,
                        modifyOrderNumber,
                        orderRequest.Items,
                        orderRequest.Promocode ? orderRequest.Promocode : '',
                    ],
                    response?.data,
                );
            },
            onError: (error, variables, context) => {
                console.log(`${context} ${error}`);
            },
        }),
    };
};

const setupAnalyticsData = (cart, reviewCart) => {
    //add analyticsData for modified cart
    window.analyticsData = window.analyticsData || {};
    window.analyticsData.page = window.analyticsData.page || {};
    window.analyticsData.page.pageTitle = 'order modification shopping cart';

    window.analyticsData.transaction = window.analyticsData.transaction || {};
    window.analyticsData.transaction.type = 'order';
    window.analyticsData.transaction.subType = 'modification';
    window.analyticsData.transaction.counter = ''; // not available: [current number transaction with same order id]

    window.analyticsData.form = window.analyticsData.form || {};
    window.analyticsData.form.formStatus = 'started';
    window.analyticsData.form.formStepNumber = '1';
    window.analyticsData.form.formStepName = 'shoppingcart';

    window.analyticsData.cart = cart; // cart contain details send by the API call
    window.analyticsData.review = reviewCart; // review contain details send by the API call
};
